import { useCallback, useState } from 'react';

import { toast } from 'sonner';
import { SurveySubmissionSource } from '@shared';
import { WaterlilyClient } from '@client/api/waterlily';

interface FormReferenceResponse {
  formReferenceId: string;
}

export function buildClientInviteLink(formReferenceId: string): string {
  return `${window.location.origin}/onboarding/${formReferenceId}?source=${SurveySubmissionSource.EMAIL}`;
}

/**
 * Copies the invite link for one client. The link resolves to that client's own
 * form reference, unlike the advisor-wide shareable link.
 */
export function useCopyClientInviteLink() {
  const [copyingClientId, setCopyingClientId] = useState<string | null>(null);

  const copyInviteLink = useCallback(async (clientId: string) => {
    setCopyingClientId(clientId);
    try {
      const { formReferenceId } = await WaterlilyClient.get<FormReferenceResponse>(
        `/client/${clientId}/form-reference`,
      );

      if (!formReferenceId) {
        throw new Error('No form reference returned');
      }

      await navigator.clipboard.writeText(buildClientInviteLink(formReferenceId));
      toast.success('Invite link copied', {
        description: 'Send this link to your client.',
      });
    } catch {
      toast.error('Could not copy the invite link', {
        description: 'Please try again or contact support.',
      });
    } finally {
      setCopyingClientId(null);
    }
  }, []);

  return { copyInviteLink, copyingClientId };
}
